// Requêtes des écrans des contrôles (TanStack Query). Clés sous « inspections » ; le changement de
// contexte vide le cache (FournisseurSession).
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../api/client';
import type {
  CarteControles,
  Constat,
  ConstatDetail,
  CreationFormulaire,
  CreationMesure,
  CreationOrdre,
  CreationPlan,
  Formulaire,
  FormulaireDetail,
  Liste,
  Mesure,
  NouvelleVersionFormulaire,
  Ordre,
  OrdreDetail,
  Plan,
  ResultatTransmission,
  SaisieConstat,
  Transmission,
} from './types';

export const INVALIDER_INSPECTIONS: readonly (readonly unknown[])[] = [['inspections'], ['indicateurs']];

const liste = <T,>(chemin: string, parametres: Record<string, string | undefined>, signal?: AbortSignal) => requete<Liste<T>>(chemin, { signal, parametres }).then((r) => r.elements);

export function useFormulaires(filtres: Record<string, string> = {}) {
  return useQuery({ queryKey: ['inspections', 'formulaires', filtres], queryFn: ({ signal }) => liste<Formulaire>('/inspections/formulaires', filtres, signal), retry: false });
}

export function useFormulaire(id: string | null) {
  return useQuery({ queryKey: ['inspections', 'formulaire', id ?? ''], queryFn: ({ signal }) => requete<FormulaireDetail>(`/inspections/formulaires/${id}`, { signal }), enabled: Boolean(id), retry: false });
}

export function usePlans() {
  return useQuery({ queryKey: ['inspections', 'plans'], queryFn: ({ signal }) => liste<Plan>('/inspections/plans', {}, signal), retry: false });
}

export function useOrdres(filtres: Record<string, string> = {}) {
  return useQuery({ queryKey: ['inspections', 'ordres', filtres], queryFn: ({ signal }) => liste<Ordre>('/inspections/ordres', filtres, signal), retry: false });
}

export function useOrdre(id: string | null) {
  return useQuery({ queryKey: ['inspections', 'ordre', id ?? ''], queryFn: ({ signal }) => requete<OrdreDetail>(`/inspections/ordres/${id}`, { signal }), enabled: Boolean(id), retry: false });
}

export function useConstat(id: string | null) {
  return useQuery({ queryKey: ['inspections', 'constat', id ?? ''], queryFn: ({ signal }) => requete<ConstatDetail>(`/inspections/constats/${id}`, { signal }), enabled: Boolean(id), retry: false });
}

export function useConflits(actif = true) {
  return useQuery({ queryKey: ['inspections', 'conflits'], queryFn: ({ signal }) => liste<Constat>('/inspections/conflits', {}, signal), enabled: actif, retry: false });
}

export function useMesures(filtres: Record<string, string> = {}) {
  return useQuery({ queryKey: ['inspections', 'mesures', filtres], queryFn: ({ signal }) => liste<Mesure>('/inspections/mesures', filtres, signal), retry: false });
}

/** Couche des ordres de mission localisés, filtrée par l'emprise affichée. */
export function useCarteControles(bbox: string | null, actif = true) {
  return useQuery({ queryKey: ['inspections', 'carte', bbox ?? ''], queryFn: ({ signal }) => requete<CarteControles>('/carte/controles', { signal, parametres: { bbox } }), enabled: actif && Boolean(bbox), retry: false });
}

// Écritures ---------------------------------------------------------------------------------

export const creerFormulaire = (corps: CreationFormulaire) => requete<FormulaireDetail>('/inspections/formulaires', { methode: 'POST', corps });
export const nouvelleVersionFormulaire = (id: string, corps: NouvelleVersionFormulaire) => requete<FormulaireDetail>(`/inspections/formulaires/${id}/versions`, { methode: 'POST', corps });
export const activerFormulaire = (id: string, actif: boolean) => requete<FormulaireDetail>(`/inspections/formulaires/${id}`, { methode: 'PATCH', corps: { actif } });
export const creerPlan = (corps: CreationPlan) => requete<Plan>('/inspections/plans', { methode: 'POST', corps });
export const creerOrdre = (corps: CreationOrdre) => requete<OrdreDetail>('/inspections/ordres', { methode: 'POST', corps });
export const demarrerOrdre = (id: string) => requete<OrdreDetail>(`/inspections/ordres/${id}/transitions/demarrer`, { methode: 'POST', corps: {} });
export const annulerOrdre = (id: string, motif: string) => requete<OrdreDetail>(`/inspections/ordres/${id}/annuler`, { methode: 'POST', corps: { motif } });
export const cloreOrdre = (id: string, motif?: string) => requete<OrdreDetail>(`/inspections/ordres/${id}/clore`, { methode: 'POST', corps: motif ? { motif } : {} });
export const reaffecterOrdre = (id: string, controleurAppartenanceId: string, motif?: string) => requete<OrdreDetail>(`/inspections/ordres/${id}/reaffecter`, { methode: 'POST', corps: { controleur_appartenance_id: controleurAppartenanceId, motif } });
export const saisirConstat = (ordreId: string, corps: SaisieConstat) => requete<ConstatDetail>(`/inspections/ordres/${ordreId}/constats`, { methode: 'POST', corps });
export const trancherConstat = (id: string, decision: 'retenir' | 'ecarter', motif?: string) => requete<ConstatDetail>(`/inspections/constats/${id}/trancher`, { methode: 'POST', corps: { decision, motif } });
export const creerMesure = (constatId: string, corps: CreationMesure) => requete<Mesure>(`/inspections/constats/${constatId}/mesures`, { methode: 'POST', corps });
export const cloturerMesure = (id: string, etat: 'realisee' | 'non_realisee', observation?: string) => requete<Mesure>(`/inspections/mesures/${id}/cloturer`, { methode: 'POST', corps: { etat, observation } });
export const transmettreConstat = (id: string, corps: Transmission) => requete<ResultatTransmission>(`/inspections/constats/${id}/transmettre`, { methode: 'POST', corps });

/** Photo (JPEG ou PNG) rattachée au constat et, si un code est donné, au point qui l'exige. */
export async function ajouterPhoto(constatId: string, fichier: File, pointCode?: string): Promise<ConstatDetail> {
  const f = new FormData();
  f.set('fichier', fichier);
  if (pointCode) f.set('point_code', pointCode);
  return requete<ConstatDetail>(`/inspections/constats/${constatId}/photos`, { methode: 'POST', corps: f });
}
